import { Icon, Row, StatColumn, Text, TextColor, TextVariant, useAppPalette, Tooltip } from "@bridgesplit/ui";
import { Divider } from "@mui/material";
import { formatPercent, lessThan, TIME } from "@bridgesplit/utils";
import { BsMetadata } from "@bridgesplit/abf-sdk";
import {
    LoopscalePointSource,
    PointsInfo,
    ExternalPointSource,
    useCollateralPointsSourcesUtil,
    calculateLtvFromLeverage,
    useRewardsCalculation
} from "@bridgesplit/abf-react";

import {
    generateLeverageStat,
    PointSourceMap,
    RenderAdditionalStat,
    TooltipContent,
    useCreateCollateralStats,
    useCreateMultiplierLoops,
    useCreateTotalMultiplierStat
} from "./common";
// Types

export interface RateDisplayProps {
    // pass in all apys to calculate the max char width
    allApys?: number[];
    showApyLabel?: boolean;
    variant?: TextVariant;
    tooltip?: "underline" | "icon";
}

interface RateAndRewardsProps {
    apy: number | undefined;
    externalPointSources: ExternalPointSource[];
    loopscalePointSources?: LoopscalePointSource[];
    metadata?: BsMetadata[];
    loopLeverage?: number;
    rateDisplayProps?: RateDisplayProps;
    totalAmount?: number;
    isSpecific?: boolean;
}

interface TooltipBreakdownProps {
    relativeMultipliers: PointsInfo[];
    collateralMultipliers?: PointsInfo[];
    totalMultipliers: PointsInfo[];
    additionalMultipliers?: PointsInfo[];
    loopscalePointSources?: LoopscalePointSource[];
    hasMultiplier: boolean;
    totalAmount: number;
    metadata?: BsMetadata[];
    isSpecific: boolean;
}

export function RateAndRewardsBreakdown({
    apy,
    externalPointSources,
    loopscalePointSources,
    metadata,
    loopLeverage,
    rateDisplayProps,
    totalAmount = 1000,
    isSpecific = false
}: RateAndRewardsProps): JSX.Element | null {
    // Hook to summarize point sources.
    const { getCheckMultiplierGreaterThanBase } = useCollateralPointsSourcesUtil();

    const { prime, secondary } = useAppPalette();

    const rewards = useRewardsCalculation({
        externalPointSources,
        loopscalePointSources,
        metadata,
        loopLeverage
    });

    const leverageStat = generateLeverageStat(loopLeverage);

    const additionalRewards = rewards.filter((p) => p.externalPointSource !== ExternalPointSource.Loopscale);
    const loopscaleRewardTotal = rewards.filter((p) => p.externalPointSource === ExternalPointSource.Loopscale);

    const relativeMultipliers = [...loopscaleRewardTotal, ...(leverageStat ? [leverageStat] : [])];

    // If RateAndRewardBreakdown has loopLeverage it means its being used for loops. We need to multiply by ltv so that we can get an accurate points calculation since principal borrowed is ltv*totalAmount.
    const ltv = loopLeverage !== undefined ? calculateLtvFromLeverage(loopLeverage) : 1;
    const totalAmountWithLtvCheck = totalAmount * ltv;

    const hasMultiplier = getCheckMultiplierGreaterThanBase(loopscalePointSources, metadata);

    const background = hasMultiplier ? prime : secondary;

    return (
        <Tooltip
            reverseColors
            title={
                <TooltipBreakdown
                    relativeMultipliers={relativeMultipliers}
                    totalMultipliers={loopscaleRewardTotal}
                    hasMultiplier={hasMultiplier}
                    additionalMultipliers={additionalRewards}
                    totalAmount={totalAmountWithLtvCheck}
                    loopscalePointSources={loopscalePointSources}
                    metadata={metadata}
                    isSpecific={isSpecific}
                />
            }
        >
            <Row spacing={0.5}>
                <PointSourceMap pointSources={externalPointSources} background={background} />
                <RateDisplay {...rateDisplayProps} apy={apy} background={background} />
            </Row>
        </Tooltip>
    );
}

function RateDisplay({
    apy,
    allApys,
    showApyLabel,
    variant,
    tooltip = "underline"
}: RateDisplayProps & { apy: number | undefined; background: string | undefined }): JSX.Element {
    const color: TextColor = lessThan(Number(apy), 0) ? "caption" : "body";
    const label = showApyLabel ? " APY" : "";
    const icon = tooltip === "icon" ? <Icon type="tooltip" /> : undefined;

    const apyExists = apy !== undefined;

    if (!allApys?.length) {
        return (
            <Text svgColor="disabled" variant={variant} underline={tooltip === "underline"} color={color}>
                {apyExists && formatPercent(apy)}
                {label}
                {icon}
            </Text>
        );
    }

    const customDigits = 3;
    const maxCharWidth = allApys.reduce((acc, apy) => Math.max(acc, formatPercent(apy, { customDigits }).length), 0);

    return (
        <Row sx={{ alignItems: "flex-end" }}>
            <Text
                svgColor="disabled"
                variant={variant}
                underline={tooltip === "underline"}
                color={color}
                sx={{ display: "inline", width: `${maxCharWidth}ch` }}
            >
                {apyExists && formatPercent(apy, { customDigits })}
                {label}
                {icon}
            </Text>
        </Row>
    );
}

function TooltipBreakdown({
    relativeMultipliers,
    collateralMultipliers,
    additionalMultipliers,
    totalMultipliers,
    loopscalePointSources,
    metadata,
    hasMultiplier,
    totalAmount,
    isSpecific
}: TooltipBreakdownProps): JSX.Element {
    const { prime, secondary } = useAppPalette();
    const { pointsPerSecond } = useCollateralPointsSourcesUtil();

    const stats = useCreateMultiplierLoops(relativeMultipliers); //Creates relative multiplier
    const collateralStats = useCreateCollateralStats(collateralMultipliers, hasMultiplier); //Creates collateral stats

    //We use idle cap since all multipliers are relative to idle cap, and the current multiplier stat is inclusive of relative multipliers
    const baseRate = (pointsPerSecond?.[LoopscalePointSource.IdleCap] ?? 0) * TIME.HOUR;

    const totalMultiplier = useCreateTotalMultiplierStat({
        loopscalePointSources,
        metadata,
        sumStat: totalMultipliers,
        baseRate,
        showMultiplier: false,
        totalAmountUsd: totalAmount,
        hasMultiplier,
        isSpecific
    });

    const background = hasMultiplier ? prime : secondary;

    return (
        <TooltipContent>
            <Row spacing={1}>
                <Text sx={{ color: background }}>
                    <Icon type="points" />
                </Text>
                <Text variant="body1">Loopscale rewards</Text>
            </Row>

            <StatColumn stats={[...stats, ...(collateralStats ?? []), ...totalMultiplier]} />
            {additionalMultipliers && additionalMultipliers.length > 0 && (
                <>
                    <Divider />
                    {additionalMultipliers.map((multiplier, index) => (
                        <RenderAdditionalStat key={index} multiplier={multiplier} />
                    ))}
                </>
            )}
        </TooltipContent>
    );
}
